/**
 * OverlayProvider — single scroll-lock and nesting registry for overlays
 * (ADR-114 §Decisión 3, LLD §4).
 *
 * Wrap the application root once:
 *
 *   <OverlayProvider><App /></OverlayProvider>
 *
 * While at least one overlay is open, the body scroll is locked exactly once
 * (counter based) and restored when the last overlay closes. If no provider
 * is mounted, overlays still work; only the scroll-lock is skipped.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

interface OverlayContextValue {
  /** Registers an open overlay; returns the unregister function. */
  register: () => () => void;
  /** Number of currently open overlays. */
  count: number;
}

const noop = () => () => {};

const OverlayContext = createContext<OverlayContextValue>({
  register: noop,
  count: 0,
});

export function OverlayProvider({ children }: { children: ReactNode }) {
  const [count, setCount] = useState(0);
  const previousOverflow = useRef<string | null>(null);

  const register = useCallback(() => {
    setCount((current) => current + 1);
    return () => setCount((current) => Math.max(0, current - 1));
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return;

    if (count > 0) {
      if (previousOverflow.current === null) {
        previousOverflow.current = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
      }
      return;
    }

    if (previousOverflow.current !== null) {
      document.body.style.overflow = previousOverflow.current;
      previousOverflow.current = null;
    }
  }, [count]);

  // Restore the body scroll if the provider unmounts with overlays open.
  useEffect(() => {
    return () => {
      if (typeof document === 'undefined') return;
      if (previousOverflow.current !== null) {
        document.body.style.overflow = previousOverflow.current;
        previousOverflow.current = null;
      }
    };
  }, []);

  const value = useMemo<OverlayContextValue>(
    () => ({ register, count }),
    [register, count],
  );

  return <OverlayContext.Provider value={value}>{children}</OverlayContext.Provider>;
}

/** Current number of open overlays (0 when no provider is mounted). */
export function useOverlayCount(): number {
  return useContext(OverlayContext).count;
}

/**
 * Registers the overlay while `open` is true. Safe to call without a
 * provider (no-op).
 */
export function useOverlayRegistration(open: boolean): void {
  const { register } = useContext(OverlayContext);

  useEffect(() => {
    if (!open) return;
    return register();
  }, [open, register]);
}
